"use client";

/**
 * Admin web i18n (ADR 0175) — typed dictionary + React context, no library.
 *
 * WHY NO LIBRARY
 * `apps/web` is a static export (ADR 0052), and next-intl's own docs state that
 * `output: 'export'` makes a locale prefix mandatory — which would mean moving
 * all 220 pages under `app/[locale]/` and changing every URL, to buy server
 * component translation that this app cannot use: 223 files are `"use client"`
 * and `AuthGate` renders nothing until client-side auth resolves. Full reasoning
 * and the react-i18next comparison are in ADR 0175 D1.
 *
 * NO FLASH — and that is not luck
 * `AuthGate` (components/AuthGate.tsx:28) returns null while auth is resolving,
 * so every page under `(dashboard)` prerenders to nothing. There is no
 * server-rendered text for a stored locale to disagree with, which is why the
 * locale can be read from localStorage synchronously in the useState initializer
 * below. The first text a user sees is already in their language.
 *
 * The driver app does the opposite — it starts at the default and reconciles in
 * an effect — because it has no such gate and must match its prerendered HTML.
 * Copying that here would introduce a flash we do not have.
 *
 * The ten pages outside AuthGate (login, register, invite, create-org,
 * select-org, onboarding, error, not-found, legal, p/*) do prerender text and
 * can hydrate against the other language. `<body suppressHydrationWarning>` in
 * app/layout.tsx already covers it, and for a static export the build-time HTML
 * is an artifact rather than a truth worth preserving.
 *
 * Audit trail: ADR 0208 — page-by-page audit (4-Archives/plan/tms/0208-*.md), scripts/i18n-page-audit.mjs
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { DEFAULT_LOCALE, setDisplayLocale, getDisplayLocale, type Locale } from "@/lib/display-locale";
import { th, type MessageKey } from "./messages/th";
import { en } from "./messages/en";

export type { Locale, MessageKey };

/** Follows the `__web_*` namespace of ADR 0065 (`__web_at`, `__web_rt`). */
const LANG_KEY = "__web_lang";

const DICTS: Record<Locale, Record<MessageKey, string>> = { th, en };

export function readStoredLocale(): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const v = window.localStorage.getItem(LANG_KEY);
    return v === "en" || v === "th" ? v : DEFAULT_LOCALE;
  } catch {
    // Private mode, blocked storage — the default is a fine answer.
    return DEFAULT_LOCALE;
  }
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: MessageKey, vars?: Record<string, string | number>) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  // Synchronous read — see the "NO FLASH" note above for why this is safe here
  // and not in the driver app.
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = readStoredLocale();
    setDisplayLocale(stored);
    return stored;
  });

  useEffect(() => {
    // `lang` drives font selection, hyphenation and screen readers. Set after
    // mount so the attribute value never differs from the prerendered HTML at
    // the moment React compares them.
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    try {
      window.localStorage.setItem(LANG_KEY, l);
    } catch {
      // Preference is lost on reload, but the session still switches.
    }
    setDisplayLocale(l);
    setLocaleState(l);
  }, []);

  const t = useCallback(
    (key: MessageKey, vars?: Record<string, string | number>): string => {
      // `?? th[key]` is unreachable while `en.ts` is typed against MessageKey —
      // it is there for the moment a dictionary is loaded some other way.
      const raw = DICTS[locale][key] ?? th[key] ?? key;
      if (!vars) return raw;
      return raw.replace(/\{(\w+)\}/g, (m, name: string) =>
        name in vars ? String(vars[name]) : m,
      );
    },
    [locale],
  );

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within <LocaleProvider>");
  return ctx;
}

/**
 * Translate a key that may not exist, falling back to a raw string.
 *
 * For status maps whose keys come from the API: a value the dictionary knows
 * gets its Thai, and an unrecognised one still shows something a human can act
 * on rather than the literal key. Kept out of `t` itself so that a mistyped
 * *static* key stays a compile error — this escape hatch is only for values
 * decided at runtime.
 */
export function translateOr(
  t: LocaleContextValue["t"],
  key: string | undefined,
  fallback: string,
): string {
  if (!key) return fallback;
  return key in th ? t(key as MessageKey) : fallback;
}


/**
 * แปลข้อความจาก "นอกคอมโพเนนต์" — ฟังก์ชันช่วยที่แปลงข้อมูล API ให้เป็นรูปที่จอใช้
 * (`mapApiVehicle`, `mapTicket`, …) เรียก `useT()` ไม่ได้ เพราะไม่ใช่คอมโพเนนต์
 * และมักถูกเรียกในลูป ซึ่งทำให้จำนวนครั้งที่เรียก hook ไม่คงที่ → React พัง
 *
 * ตัวนี้อ่านภาษาจาก `display-locale` ตัวเดียวกับที่ provider เขียนไว้ จึงได้ค่า
 * ปัจจุบันเสมอ และเมื่อผู้ใช้สลับภาษา provider จะ re-render คอมโพเนนต์ที่เรียก
 * ฟังก์ชันช่วยเหล่านี้อยู่แล้ว ข้อความจึงเปลี่ยนตาม
 */
export function tOutside(key: MessageKey, vars?: Record<string, string | number>): string {
  const raw = DICTS[getDisplayLocale()][key] ?? th[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

/** The translate function alone: `const t = useT(); t("nav.tickets")`. */
export function useT(): LocaleContextValue["t"] {
  return useLocale().t;
}
